import { useEffect, useState } from 'react';
import client from '../../shared/api/client';
import {
  canSubmitCustomFieldValue,
  customFieldValueToParams,
} from '../CustomFieldValues/actions/customFieldValuesActionCreators';
import { sharedAttributionVisitorId, useAttributionVisitor } from '../../shared/attribution/useAttributionVisitor';

export const ORDER_TOKEN_PARAM = 'series_pass_order';
const ORDER_TOKEN_FORMAT = /^[A-Za-z0-9]+$/;

const urlParams = () => new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);

const returnedOrderToken = () => {
  const token = urlParams().get(ORDER_TOKEN_PARAM);
  return token && ORDER_TOKEN_FORMAT.test(token) ? token : null;
};

const initialPhase = () => {
  if (typeof window === 'undefined') return 'details';
  if (urlParams().get('redirect_status') !== 'succeeded') return 'details';
  if (returnedOrderToken()) return 'completing';

  return 'thankYou';
};

const rememberOrderToken = (token) => {
  const url = new URL(window.location.href);
  url.searchParams.set(ORDER_TOKEN_PARAM, token);
  window.history.replaceState(window.history.state, '', url.toString());
};

const forgetOrderToken = () => {
  const url = new URL(window.location.href);
  [ORDER_TOKEN_PARAM, 'payment_intent', 'payment_intent_client_secret', 'redirect_status'].forEach((param) => url.searchParams.delete(param));
  window.history.replaceState(window.history.state, '', url.toString());
};

const GIFT_CARD_CHECKOUT_FIELDS = [
  'applied_gift_card_summaries',
  'gift_card_adjusted_total',
  'stripe_required',
  'gift_card_required_card_payment_cents',
  'gift_card_entry_closed',
];

const giftCardCheckoutFrom = (data) =>
  GIFT_CARD_CHECKOUT_FIELDS.reduce((picked, field) => ({ ...picked, [field]: data[field] }), {});

const COMPLETION_UNREACHABLE_MESSAGE = "We're still confirming your payment. Check your email for your pass in a few minutes, or try again.";
const FULL_COVER_UNREACHABLE_MESSAGE = "We couldn't confirm your purchase. Please try again in a moment.";

const CHECKBOX_ANSWER_TYPE = 'CustomFieldValues::Checkbox';
const CHECKED_ANSWER = 'true';

const passAnswerComplete = (customFieldValue) => {
  if (customFieldValue.custom_field.required && customFieldValue.type === CHECKBOX_ANSWER_TYPE) {
    return customFieldValue.value === CHECKED_ANSWER;
  }

  return canSubmitCustomFieldValue(customFieldValue);
};

const sendJson = async (url, method, data) => {
  const response = await client.request({ url, method, data, validateStatus: () => true });
  return { ok: response.status >= 200 && response.status < 300, data: response.data || {} };
};

// The email shape check is intentionally loose (x@y.z): the submit is a plain
// button, not a native <form>, so the input's type="email" validation never
// fires — without this a typo'd address gets bound to a live pass order.
const emailValid = (email) => /\S+@\S+\.\S+/.test(email.trim());

// A self-serve ceiling on one order. Passes are high-value and each unit mints a
// real credential (a Season Pass pre-mints an admission per show), so an
// unbounded picker is both a support problem and a way to burn a cap in one
// request. A venue selling a block beyond this does it as an organizer action.
const MAX_PER_ORDER = 10;

export default function useSeriesPassCheckoutState({ products, seriesPassesCreateUrl, venueAgreements = [] }) {
  // Nothing is selected at mount — not even a lone product. The order summary
  // portals into the always-visible sidebar, so auto-selecting a single product
  // populated it before the buyer had even opened the checkout. Start empty and
  // let the buyer pick, exactly like the multi-product flow.
  const [selectedProductId, setSelectedProductId] = useState(null);
  const attributionVisitorUuid = useAttributionVisitor('od_embed.js');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [contactZip, setContactZip] = useState('');
  const [subscribeToVenue, setSubscribeToVenue] = useState(false);
  const [acceptedAgreements, setAcceptedAgreements] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [passAnswers, setPassAnswers] = useState({});

  const [phase, setPhase] = useState(initialPhase);
  const [clientSecret, setClientSecret] = useState(null);
  const [orderNumber, setOrderNumber] = useState(null);
  const [orderToken, setOrderToken] = useState(returnedOrderToken);
  const [completionRequired, setCompletionRequired] = useState(() => !!returnedOrderToken());
  const [giftCardCheckout, setGiftCardCheckout] = useState(null);
  const [paymentFormVersion, setPaymentFormVersion] = useState(0);
  const [completionError, setCompletionError] = useState(null);
  const [mustRestart, setMustRestart] = useState(false);
  const [giftCardPending, setGiftCardPending] = useState(false);
  const [cardSubmitting, setCardSubmitting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const selectedProduct = (products || []).find((p) => p.id === selectedProductId) || null;
  // The email is the only delivery path for a no-account buyer, so require a
  // matching re-entry (mirrors the regular checkout's confirm-email field).
  const emailsMatch =
    emailValid(email) && email.trim().toLowerCase() === confirmEmail.trim().toLowerCase();
  // The product's already-resolved per-order cap (organizer value clamped to the
  // platform ceiling, computed server-side); the constant is only the fallback
  // for the moment before a product is selected. NOT capped at the product's
  // remaining count: the public payload deliberately does not publish live
  // inventory, and a client-side cap would be stale the moment someone else buys.
  // Asking for more than is left returns a clean 422 naming the real count, and
  // the locked decrement at paid time stays the authoritative gate.
  const maxQuantity = selectedProduct?.maxPerOrder ?? MAX_PER_ORDER;

  // A lower per-product cap must not strand a quantity chosen for a higher-capped
  // product. Clamp during render (not in an effect) so a downward product switch
  // never paints the Qty <select> with an out-of-range value for a frame: React
  // re-renders in place before paint, and the guard keeps it from looping. Never
  // auto-raises.
  if (quantity > maxQuantity) {
    setQuantity(maxQuantity);
  }

  const allAgreementsAccepted = venueAgreements.every(
    (agreement) => agreement.required === false || !!acceptedAgreements[agreement.id]
  );

  const acceptedVenueAgreementIds = venueAgreements
    .filter((agreement) => !!acceptedAgreements[agreement.id])
    .map((agreement) => agreement.id);

  const checkoutQuestions = selectedProduct?.checkoutQuestions || [];
  const passIndexes = Array.from({ length: quantity }, (_, passIndex) => passIndex);

  const answersForPass = (passIndex) =>
    checkoutQuestions.map((question) => {
      const answerKey = `${passIndex}:${question.custom_field.id}`;
      return answerKey in passAnswers ? { ...question, value: passAnswers[answerKey] } : question;
    });

  const setPassAnswer = (passIndex, customFieldValue) =>
    setPassAnswers((current) => ({
      ...current,
      [`${passIndex}:${customFieldValue.custom_field.id}`]: customFieldValue.value,
    }));

  const passQuestionsComplete = passIndexes.every((passIndex) =>
    answersForPass(passIndex).every(passAnswerComplete)
  );

  const customFieldValues = passIndexes.flatMap((passIndex) =>
    answersForPass(passIndex).map((customFieldValue) => ({
      pass_index: passIndex,
      custom_field_id: customFieldValue.custom_field.id,
      value: customFieldValueToParams(customFieldValue).value,
    }))
  );

  const detailsComplete =
    allAgreementsAccepted &&
    !!selectedProduct &&
    !!firstName.trim() &&
    !!lastName.trim() &&
    emailsMatch &&
    contactZip.trim().length > 0 &&
    quantity >= 1 &&
    quantity <= maxQuantity &&
    passQuestionsComplete;

  const displayedUnitTotal = selectedProduct?.priceBreakdown?.total;

  const submitPurchase = async () => {
    if (!detailsComplete || submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const { ok, data } = await sendJson(seriesPassesCreateUrl, 'POST', {
        series_pass: {
          series_pass_product_id: selectedProductId,
          first_name: firstName,
          last_name: lastName,
          email,
          contact_zip: contactZip,
          subscribe_to_venue: subscribeToVenue,
          quantity,
          expected_unit_total: displayedUnitTotal != null ? Number(displayedUnitTotal) : undefined,
          accepted_venue_agreement_ids: acceptedVenueAgreementIds,
          custom_field_values: customFieldValues,
        },
        attribution_visitor_uuid: attributionVisitorUuid || sharedAttributionVisitorId() || '',
      });

      if (!ok) {
        setError((data.errors || ['Something went wrong. Please try again.']).join(' '));
        setSubmitting(false);
        return;
      }

      setClientSecret(data.stripe_client_secret);
      setOrderNumber(data.order_number);
      setOrderToken(data.token);
      setCompletionRequired(!!data.completion_required);
      setGiftCardCheckout(giftCardCheckoutFrom(data));
      if (data.completion_required) rememberOrderToken(data.token);
      setPhase('payment');
    } catch (e) {
      setError('Could not reach the server. Please try again.');
    }

    setSubmitting(false);
  };

  const orderUrl = (path) => `${seriesPassesCreateUrl}/${encodeURIComponent(orderToken)}/${path}`;

  const giftCardCheckoutChanged = (data) => {
    setGiftCardCheckout(giftCardCheckoutFrom(data));
    setPaymentFormVersion((version) => version + 1);
  };

  const withGiftCardPending = async (request) => {
    setGiftCardPending(true);
    try {
      return await request();
    } finally {
      setGiftCardPending(false);
    }
  };

  const applyGiftCard = (code, getTurnstileToken = async () => '') => withGiftCardPending(async () => {
    const { ok, data } = await sendJson(orderUrl('gift_card_redemptions'), 'POST', {
      gift_card_code: code,
      turnstile_token: await getTurnstileToken(),
    });
    if (ok) giftCardCheckoutChanged(data);

    return { ok, error: (data.errors || [])[0], captchaRequired: !!data.captcha_required };
  });

  const removeGiftCard = (redemptionId) => withGiftCardPending(async () => {
    const { ok, data } = await sendJson(orderUrl(`gift_card_redemptions/${redemptionId}`), 'DELETE');
    if (ok) giftCardCheckoutChanged(data);

    return { ok, error: (data.errors || [])[0], releasedCodeLast4s: data.released_gift_card_code_last4s || [] };
  });

  const fullyCoveredByGiftCards = !!giftCardCheckout
    && (giftCardCheckout.applied_gift_card_summaries || []).length > 0
    && !(giftCardCheckout.stripe_required ?? true);

  const requestCompletion = async () => {
    try {
      return await sendJson(orderUrl('completion'), 'POST');
    } catch (e) {
      return { ok: false, data: {} };
    }
  };

  const applyCompletion = ({ ok, data }) => {
    if (ok) {
      forgetOrderToken();
      setPhase('thankYou');
      return;
    }
    setMustRestart(!!data.must_restart);
    setCompletionError((data.errors || [fullyCoveredByGiftCards ? FULL_COVER_UNREACHABLE_MESSAGE : COMPLETION_UNREACHABLE_MESSAGE])[0]);
    setPhase('completionFailed');
  };

  useEffect(() => {
    if (phase !== 'completing') return undefined;
    let ignore = false;
    requestCompletion().then((result) => {
      if (!ignore) applyCompletion(result);
    });
    return () => {
      ignore = true;
    };
  }, []);

  const finishCheckout = async () => {
    setCompletionError(null);
    setPhase('completing');
    applyCompletion(await requestCompletion());
  };

  const onPaymentSuccess = () => {
    if (!completionRequired) {
      setPhase('thankYou');
      return;
    }

    finishCheckout();
  };

  const reset = () => {
    forgetOrderToken();
    setPhase('details');
    setClientSecret(null);
    setOrderNumber(null);
    setOrderToken(null);
    setCompletionRequired(false);
    setGiftCardCheckout(null);
    setCompletionError(null);
    setMustRestart(false);
    setGiftCardPending(false);
    setCardSubmitting(false);
    setSubmitting(false);
    setError(null);
    setSelectedProductId(null);
    setFirstName('');
    setLastName('');
    setEmail('');
    setConfirmEmail('');
    setContactZip('');
    setSubscribeToVenue(false);
    setAcceptedAgreements({});
    setQuantity(1);
    setPassAnswers({});
  };

  return {
    selectedProductId,
    setSelectedProductId,
    selectedProduct,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    confirmEmail,
    setConfirmEmail,
    contactZip,
    setContactZip,
    subscribeToVenue,
    setSubscribeToVenue,
    acceptedAgreements,
    setAcceptedAgreements,
    quantity,
    setQuantity,
    maxQuantity,
    checkoutQuestions,
    answersForPass,
    setPassAnswer,
    emailsMatch,
    phase,
    clientSecret,
    orderNumber,
    submitting,
    error,
    detailsComplete,
    submitPurchase,
    onPaymentSuccess,
    reset,
    giftCardCheckout,
    paymentFormVersion,
    applyGiftCard,
    removeGiftCard,
    finishCheckout,
    completionError,
    mustRestart,
    giftCardPending,
    cardSubmitting,
    setCardSubmitting,
    fullyCoveredByGiftCards,
  };
}
